import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { Form, InputGroup } from "react-bootstrap";
import RequiredLabel from "../../CustomCommons/RequiredLabel";
import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";
import { logout_func2 } from "../../Const/logoutCommon";
import { confirmAndRemoveMedia } from "../HeroBanner/mediaRemovalHelper";

const CreateCategoryBanner = () => {
  const history = useHistory();

  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryQuery, setCategoryQuery] = useState("");

  const [bannerTitle, setBannerTitle] = useState("");
  const [mediaType, setMediaType] = useState(0); // 0 = image, 1 = video
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [order, setOrder] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [bannerId, setBannerId] = useState(null);
  const [bannerName, setBannerName] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");

  const [mobileBannerId, setMobileBannerId] = useState(null);
  const [mobileBannerName, setMobileBannerName] = useState("");
  const [mobileBannerPreview, setMobileBannerPreview] = useState("");

  const [videoId, setVideoId] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [videoPreview, setVideoPreview] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setOnlogoutHide] = useState(0);

  const uploadDocument = (file, onSuccess, docType = 0) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    formData.append("doc_type", docType);

    axios
      .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
      .then((res) => {
        if (res?.data?.status) {
          onSuccess(res?.data?.data?.id);
          showToast("success", "File uploaded successfully");
        } else {
          showToast("error", "Failed to upload file");
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.errors?.document?.[0] ||
          err?.response?.data?.message ||
          "Upload failed";
        showToast("error", message);
      });
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(file, (id) => {
      setBannerId(id);
      setBannerName(file.name);
      const reader = new FileReader();
      reader.onload = () => setBannerPreview(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const handleMobileBannerUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(file, (id) => {
      setMobileBannerId(id);
      setMobileBannerName(file.name);
      const reader = new FileReader();
      reader.onload = () => setMobileBannerPreview(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(
      file,
      (id) => {
        setVideoId(id);
        setVideoName(file.name);
      },
      1
    );
    const reader = new FileReader();
    reader.onload = () => setVideoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const resetMedia = () => {
    setBannerId(null);
    setBannerName("");
    setBannerPreview("");
    setMobileBannerId(null);
    setMobileBannerName("");
    setMobileBannerPreview("");
    setVideoId(null);
    setVideoName("");
    setVideoPreview("");
  };

  const handleRemoveMedia = (type) => {
    confirmAndRemoveMedia({
      onConfirm: () => {
        if (type === "banner") {
          setBannerId(null);
          setBannerName("");
          setBannerPreview("");
        } else if (type === "mobileBanner") {
          setMobileBannerId(null);
          setMobileBannerName("");
          setMobileBannerPreview("");
        } else if (type === "video") {
          setVideoId(null);
          setVideoName("");
          setVideoPreview("");
        }
      },
    });
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      const params = new URLSearchParams();
      params.append("limit", 20);
      params.append("is_website", true);
      if (categoryQuery) {
        params.append("query", categoryQuery);
      }

      axios
        .get(
          `${BASE_URL}api/v1/inventory/inventory/category/?${params.toString()}`
        )
        .then((res) => {
          const results = res?.data?.data?.results || [];
          const formatted = results.map((item) => ({
            label: item?.name,
            value: item?.id,
          }));
          setCategoryOptions(formatted);
        })
        .catch(() => {});
    }, 300);

    return () => clearTimeout(debounce);
  }, [categoryQuery]);

  const validateForm = () => {
    if (!bannerTitle.trim()) {
      showToast("error", "Banner title is required.");
      return false;
    }
    if (!categories.length) {
      showToast("error", "Please select at least one category.");
      return false;
    }
    if (!order && order !== 0) {
      showToast("error", "Order is required.");
      return false;
    }
    if (Number(mediaType) === 0) {
      if (!bannerId) {
        showToast("error", "Please upload a banner image.");
        return false;
      }
      if (!mobileBannerId) {
        showToast("error", "Please upload a mobile banner image.");
        return false;
      }
    }
    if (Number(mediaType) === 1 && !videoId) {
      showToast("error", "Please upload a banner video.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    // Helper function to convert datetime-local string to ISO string
    const convertToISO = (dateTimeString) => {
      if (!dateTimeString || !dateTimeString.trim()) return null;
      const trimmed = dateTimeString.trim();
      // datetime-local format is "YYYY-MM-DDTHH:mm" (16 chars), we need to append seconds
      let dateTimeWithSeconds = trimmed;
      if (trimmed.length === 16 && trimmed.includes("T") && trimmed.includes(":")) {
        // Format: "YYYY-MM-DDTHH:mm" -> "YYYY-MM-DDTHH:mm:00"
        dateTimeWithSeconds = `${trimmed}:00`;
      }
      // Create date object - this will interpret as local time
      const date = new Date(dateTimeWithSeconds);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateTimeWithSeconds);
        return null;
      }
      return date.toISOString();
    };

    const payload = {
      categories: categories.map((cat) => cat.value),
      category: categories.map((cat) => cat.value),
      banner_title: bannerTitle.trim(),
      media_type: Number(mediaType),
      start_at: convertToISO(startAt),
      end_at: convertToISO(endAt),
      is_default: Boolean(isDefault),
      order: Number(order),
      is_active: Boolean(isActive),
      banner: Number(mediaType) === 0 ? bannerId : null,
      mobile_banner: Number(mediaType) === 0 ? mobileBannerId : null,
      video: Number(mediaType) === 1 ? videoId : null,
    };

    setIsSubmitting(true);
    axios
      .post(`${BASE_URL}api/v1/frontend/admin/v2/custom-categories/`, payload)
      .then(() => {
        showToast("success", "Category banner created successfully.");
        history.push("/website-settings/category-banner-list");
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        const message =
          err?.response?.data?.errors?.non_field_errors?.[0] ||
          err?.response?.data?.message ||
          "Failed to create category banner. Please try again.";
        showToast("error", message);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    history.push("/website-settings/category-banner-list");
  };

  const mediaToggleLabel = useMemo(
    () => (Number(mediaType) === 0 ? "Image" : "Video"),
    [mediaType]
  );

  return (
    <div className="categorey-parent new-categorey-parent an">
      <div className="bg-white add-div">
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "20px 35px", border: "0.5px solid #E0E0E0" }}
        >
          <h3 style={{ fontSize: "23px", marginTop: "6px" }}>
            Create Category Banner
          </h3>
        </div>

        <div
          style={{
            padding: "20px 35px",
            borderBottom: "0.5px solid #E0E0E0",
            borderRight: "0.5px solid #E0E0E0",
            borderLeft: "0.5px solid #E0E0E0",
          }}
          className="row"
        >
          <div className="col-12 col-md-6">
            <RequiredLabel text="Banner Title" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Banner Title"
                style={{ backgroundColor: "#FAFAFA" }}
                value={bannerTitle}
                onChange={(e) => setBannerTitle(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Order" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Order"
                style={{ backgroundColor: "#FAFAFA" }}
                value={order}
                type="number"
                min={0}
                onChange={(e) => setOrder(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Start At" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                type="datetime-local"
                style={{ backgroundColor: "#FAFAFA" }}
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="End At" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                type="datetime-local"
                style={{ backgroundColor: "#FAFAFA" }}
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Categories" />
            <div className="position-relative">
              <Select
                isMulti
                placeholder="Select categories"
                value={categories}
                onChange={(value) => setCategories(value || [])}
                onInputChange={(value) => setCategoryQuery(value)}
                options={categoryOptions}
              />
            </div>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Media Type" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Select
                aria-label="Media Type"
                style={{ backgroundColor: "#FAFAFA" }}
                value={mediaType}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMediaType(value);
                  resetMedia();
                }}
              >
                <option value={0}>Image</option>
                <option value={1}>Video</option>
              </Form.Select>
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Is Default" />
            <div
              className="d-flex justify-content-between align-items-center py-3 px-3 mb-3"
              style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
            >
              <span>{isDefault ? "Yes" : "No"}</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              </div>
            </div>
          </div>

          {Number(mediaType) === 0 && (
            <>
              <div className="col-12">
                <RequiredLabel text="Banner Image (Desktop)" />
                <p style={{ marginTop: "10px", color: "red" }}>
                  NB: Upload image in (height-420px, width-1920px) for Banner.
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {bannerName ? "File Chosen" : "No File Chosen"}
                  </p>
                  <div className="file-up">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerUpload}
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {bannerPreview && (
                  <div className="my-2">
                    <img
                      height={90}
                      width={90}
                      src={bannerPreview}
                      alt="banner"
                    />
                    <br />
                    <button
                      onClick={() => handleRemoveMedia("banner")}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="col-12 mt-3">
                <RequiredLabel text="Banner Image (Mobile)" />
                <p style={{ marginTop: "10px", color: "red" }}>
                  NB: Upload image in (height-250px, width-360px) for Banner.
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {mobileBannerName ? "File Chosen" : "No File Chosen"}
                  </p>
                  <div className="file-up">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMobileBannerUpload}
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {mobileBannerPreview && (
                  <div className="my-2">
                    <img
                      height={90}
                      width={90}
                      src={mobileBannerPreview}
                      alt="mobile banner"
                    />
                    <br />
                    <button
                      onClick={() => handleRemoveMedia("mobileBanner")}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {Number(mediaType) === 1 && (
            <div className="col-12">
              <RequiredLabel text={`Banner ${mediaToggleLabel}`} />
              <div
                className="w-100 browse-main d-flex align-items-center"
                style={{
                  height: "55px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "7px",
                  overflowX: "hidden",
                }}
              >
                <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                  {videoName ? "File Chosen" : "No File Chosen"}
                </p>
                <div className="file-up">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                  <p>Browse</p>
                </div>
              </div>
              {videoPreview && (
                <div className="my-2">
                  <video height={120} width={160} src={videoPreview} controls />
                  <br />
                  <button
                    onClick={() => handleRemoveMedia("video")}
                    className="remove-btn btn btn-small mt-1 rounded"
                    style={{ border: "1px solid gray !important" }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="col-12 mt-4">
            <h5>Status</h5>
            <p style={{ color: "#8E8E93" }}>( Toggle visibility )</p>
            <div
              className="d-flex justify-content-between align-items-center py-3 px-3"
              style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
            >
              <span>{isActive ? "Active" : "Inactive"}</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSubmit}
              className="save-btn me-2 mt-2"
              style={{ background: "#000", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              className="save-btn mt-2"
              style={{ border: ".4px solid #E0E0E0 !important" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCategoryBanner;
